import { useCallback, useEffect, useState } from "react";
import Body from "../components/layout/Body";
import useApiHook from "../hooks/useApiHook";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField
} from "@mui/material";
import Typography from "@mui/material/Typography";
import { DataGrid } from "@mui/x-data-grid";

const formatCurrency = (amountPaid, currency = "EUR") => {
    const amount = Number.isFinite(amountPaid) ? amountPaid / 100 : 0;
    const normalizedCurrency = typeof currency === "string"
        ? currency.trim().toUpperCase()
        : "EUR";

    const safeCurrency = /^[A-Z]{3}$/.test(normalizedCurrency)
        ? normalizedCurrency
        : "EUR";

    return new Intl.NumberFormat("it-IT", {
        style: "currency",
        currency: safeCurrency
    }).format(amount);
};

const formatDate = (value) => {
    if (!value) {
        return "-";
    }

    return new Intl.DateTimeFormat("it-IT", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    }).format(new Date(value));
};

const getInvoiceStatusMeta = (status) => {
    switch (status) {
        case "issued":
            return { label: "Inviata", color: "success" };
        case "requested":
            return { label: "Richiesta", color: "warning" };
        default:
            return { label: "Non richiesta", color: "default" };
    }
};

const companyTypeOptions = [
    { value: "forfettario", label: "Regime forfettario" },
    { value: "ordinario_semplificata", label: "Regime ordinario (semplificata)" },
    { value: "ordinario", label: "Regime ordinario" }
];

const telematicAddressTypeOptions = [
    { value: "sdi", label: "Codice SDI" },
    { value: "pec", label: "PEC" }
];

const BillingProfileSection = () => {
    const { makeRequest, loading } = useApiHook();
    const [form, setForm] = useState({
        billingType: "individual",
        fullName: "",
        companyName: "",
        companyFiscalRegime: "",
        taxCode: "",
        vatNumber: "",
        address: "",
        city: "",
        zipCode: "",
        country: "IT",
        telematicAddressType: "sdi",
        telematicAddress: ""
    });
    const [isComplete, setIsComplete] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const isCompanyProfile = form.billingType === "company";
    const companyNeedsElectronicAddress = isCompanyProfile && !form.telematicAddress.trim();

    useEffect(() => {
        const loadProfile = async () => {
            const data = await makeRequest("/billing/profile");
            if (!data || data.state !== "success") {
                return;
            }

            const profile = data.data.profile;
            if (profile) {
                setForm({
                    billingType: profile.billingType || "individual",
                    fullName: profile.fullName || "",
                    companyName: profile.companyName || "",
                    companyFiscalRegime: profile.companyFiscalRegime || "",
                    taxCode: profile.taxCode || "",
                    vatNumber: profile.vatNumber || "",
                    address: profile.address || "",
                    city: profile.city || "",
                    zipCode: profile.zipCode || "",
                    country: profile.country || "IT",
                    telematicAddressType: profile.telematicAddressType || "sdi",
                    telematicAddress: profile.telematicAddress || ""
                });
            }
            setIsComplete(Boolean(data.data.isComplete));
        };

        loadProfile();
    }, [makeRequest]);

    const onChange = (field) => (event) => {
        setForm((current) => ({
            ...current,
            [field]: event.target.value
        }));
    };

    const onSubmit = async (event) => {
        event.preventDefault();
        setFeedback(null);

        const data = await makeRequest("/billing/profile", "put", form);
        if (!data) {
            setFeedback({ type: "error", message: "Salvataggio non riuscito." });
            return;
        }

        if (data.state === "success") {
            setIsComplete(Boolean(data.data.isComplete));
            setFeedback({ type: "success", message: "Dati fiscali aggiornati." });
            return;
        }

        setFeedback({ type: "error", message: data.error?.message || "Salvataggio non riuscito." });
    };

    return (
        <Paper elevation={0} sx={{ border: "1px solid #e0e0e0", p: 3 }}>
            <Stack spacing={2}>
                <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "start", sm: "center" }} justifyContent="space-between" spacing={2}>
                    <Typography variant="h6">Dati fiscali</Typography>
                    <Chip color={isComplete ? "success" : "default"} label={isComplete ? "Profilo completo" : "Profilo incompleto"} />
                </Stack>

                {feedback && <Alert severity={feedback.type}>{feedback.message}</Alert>}

                <Box component="form" onSubmit={onSubmit}>
                    <Stack spacing={2}>
                        <FormControl fullWidth>
                            <InputLabel id="billing-type-label">Tipo profilo</InputLabel>
                            <Select
                                labelId="billing-type-label"
                                value={form.billingType}
                                label="Tipo profilo"
                                onChange={onChange("billingType")}
                            >
                                <MenuItem value="individual">Privato</MenuItem>
                                <MenuItem value="company">Azienda</MenuItem>
                            </Select>
                        </FormControl>

                        {!isCompanyProfile && (
                            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Nome e cognome"
                                    value={form.fullName}
                                    onChange={onChange("fullName")}
                                />
                                <TextField
                                    fullWidth
                                    required
                                    label="Codice fiscale"
                                    value={form.taxCode}
                                    onChange={onChange("taxCode")}
                                />
                            </Stack>
                        )}

                        {isCompanyProfile && (
                            <>
                                <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="Ragione sociale"
                                        value={form.companyName}
                                        onChange={onChange("companyName")}
                                    />
                                    <FormControl fullWidth required>
                                        <InputLabel id="company-fiscal-regime-label">Tipo azienda</InputLabel>
                                        <Select
                                            labelId="company-fiscal-regime-label"
                                            value={form.companyFiscalRegime}
                                            label="Tipo azienda"
                                            onChange={onChange("companyFiscalRegime")}
                                        >
                                            {companyTypeOptions.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Stack>
                                <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="Partita IVA"
                                        value={form.vatNumber}
                                        onChange={onChange("vatNumber")}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Referente"
                                        value={form.fullName}
                                        onChange={onChange("fullName")}
                                    />
                                </Stack>
                            </>
                        )}

                        <TextField fullWidth required label="Indirizzo" value={form.address} onChange={onChange("address")} />

                        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                            <TextField fullWidth required label="Citta" value={form.city} onChange={onChange("city")} />
                            <TextField fullWidth required label="CAP" value={form.zipCode} onChange={onChange("zipCode")} />
                            <TextField fullWidth required label="Paese" value={form.country} onChange={onChange("country")} />
                        </Stack>

                        {isCompanyProfile && (
                            <>
                                <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                                    <FormControl fullWidth required error={companyNeedsElectronicAddress}>
                                        <InputLabel id="telematic-address-type-label">Tipo indirizzo telematico</InputLabel>
                                        <Select
                                            labelId="telematic-address-type-label"
                                            value={form.telematicAddressType}
                                            label="Tipo indirizzo telematico"
                                            onChange={onChange("telematicAddressType")}
                                        >
                                            {telematicAddressTypeOptions.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        fullWidth
                                        error={companyNeedsElectronicAddress}
                                        required
                                        label="Indirizzo telematico"
                                        value={form.telematicAddress}
                                        onChange={onChange("telematicAddress")}
                                    />
                                </Stack>
                                <Typography variant="body2" color={companyNeedsElectronicAddress ? "error" : "text.secondary"}>
                                    Per il profilo azienda serve un indirizzo telematico con tipo Codice SDI o PEC.
                                </Typography>
                            </>
                        )}

                        <Box>
                            <Button type="submit" variant="contained" disabled={loading}>
                                {loading ? "Salvataggio..." : "Salva dati fiscali"}
                            </Button>
                        </Box>
                    </Stack>
                </Box>
            </Stack>
        </Paper>
    );
};

const PaymentsTableSection = () => {
    const { makeRequest, loading } = useApiHook();
    const [payments, setPayments] = useState([]);
    const [feedback, setFeedback] = useState(null);
    const [requestingId, setRequestingId] = useState(null);

    const loadPayments = useCallback(async () => {
        const data = await makeRequest("/billing/payments");
        if (!data) {
            setFeedback({ type: "error", message: "Impossibile caricare i pagamenti." });
            return;
        }

        if (data.state === "success") {
            setPayments(data.data);
            return;
        }

        setFeedback({ type: "error", message: data.error?.message || "Impossibile caricare i pagamenti." });
    }, [makeRequest]);

    useEffect(() => {
        loadPayments();
    }, [loadPayments]);

    const onRequestInvoice = async (paymentId) => {
        setFeedback(null);
        setRequestingId(paymentId);

        const data = await makeRequest(`/billing/payments/${paymentId}/request-invoice`, "post");

        if (!data) {
            setFeedback({ type: "error", message: "Richiesta fattura non riuscita." });
            setRequestingId(null);
            return;
        }

        if (data.state === "success") {
            setFeedback({ type: "success", message: "Richiesta fattura inviata." });
            await loadPayments();
            setRequestingId(null);
            return;
        }

        setFeedback({ type: "error", message: data.error?.message || "Richiesta fattura non riuscita." });
        setRequestingId(null);
    };

    const rows = payments.map((payment) => ({
        ...payment,
        packageLabel: payment.packageName || payment.packageId,
        paymentStatusLabel: payment.status === "paid" ? "Pagato" : payment.status,
        invoiceMeta: getInvoiceStatusMeta(payment.invoiceStatus)
    }));

    const columns = [
        {
            field: "purchasedAt",
            headerName: "Data",
            minWidth: 180,
            flex: 1,
            valueFormatter: (value) => formatDate(value)
        },
        {
            field: "packageLabel",
            headerName: "Pacchetto",
            minWidth: 180,
            flex: 1.2
        },
        {
            field: "amount",
            headerName: "Importo",
            minWidth: 130,
            flex: 0.8,
            renderCell: ({ row }) => formatCurrency(row.amountPaid, row.currency)
        },
        {
            field: "paymentStatusLabel",
            headerName: "Pagamento",
            minWidth: 130,
            flex: 0.8,
            renderCell: ({ row }) => (
                <Chip
                    size="small"
                    color={row.status === "paid" ? "success" : "default"}
                    label={row.paymentStatusLabel}
                />
            )
        },
        {
            field: "invoiceMeta",
            headerName: "Fattura",
            minWidth: 140,
            flex: 0.8,
            renderCell: ({ value }) => (
                <Chip size="small" color={value.color} label={value.label} />
            )
        },
        {
            field: "actions",
            headerName: "Azioni",
            minWidth: 170,
            flex: 0.9,
            sortable: false,
            filterable: false,
            renderCell: ({ row }) => {
                const canRequestInvoice = row.status === "paid" && row.invoiceStatus === "not_requested";

                return (
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={!canRequestInvoice || requestingId === row.id}
                        onClick={() => onRequestInvoice(row.id)}
                    >
                        {requestingId === row.id ? "Invio..." : "Richiedi fattura"}
                    </Button>
                );
            }
        }
    ];

    const noRowsOverlay = () => (
        <Stack height="100%" alignItems="center" justifyContent="center">
            <Typography color="text.secondary">Nessun pagamento trovato.</Typography>
        </Stack>
    );

    return (
        <Paper elevation={0} sx={{ border: "1px solid #e0e0e0", p: 3 }}>
            <Stack spacing={2}>
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "start", sm: "center" }} spacing={2}>
                    <Typography variant="h6">Pagamenti</Typography>
                    {loading && <CircularProgress size={22} />}
                </Stack>

                {feedback && <Alert severity={feedback.type}>{feedback.message}</Alert>}

                <Box>
                    <DataGrid
                        autoHeight
                        rows={rows}
                        columns={columns}
                        disableRowSelectionOnClick
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 10
                                }
                            }
                        }}
                        pageSizeOptions={[5, 10, 15]}
                        slots={{
                            noRowsOverlay
                        }}
                    />
                </Box>
            </Stack>
        </Paper>
    );
};

const BillingContent = () => {
    return (
        <Stack spacing={3}>
            <Box>
                <Typography variant="h4" fontWeight="bold">Pagamenti e fatture</Typography>
                <Typography color="text.secondary" mt={1}>
                    Gestisci storico acquisti e dati fiscali del tuo account.
                </Typography>
            </Box>
            <PaymentsTableSection />
            <Divider />
            <BillingProfileSection />
        </Stack>
    );
};

const BillingPage = () => {
    return <Body mainElement={<BillingContent />} />;
};

export default BillingPage;
